import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Bell, Phone, Mail, MessageCircle, UserCheck, Check, CheckCheck, X, Briefcase, FileText, Trash2, AlarmClock, ExternalLink, MoonStar, Clock, CalendarDays } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { toast } from "sonner";
import { formatDate, localizeIsoDatesInText } from "@/utils/helpers";
import api from "@/api";

// ─── Anti-spam persistent state (localStorage-backed) ───────────────────
// Bug fix 22/02/2026: lo snooze ora vive sul BACKEND (campo
// `snoozedUntil` nel documento notifica). localStorage resta come
// cache UX best-effort SOLAMENTE per evitare flicker di re-pop tra
// il click "Rimanda" e il prossimo polling. La verità è il backend:
// il filtro `GET /api/notifications` esclude automaticamente le
// notifiche con `snoozedUntil > now`, quindi anche se localStorage è
// vuoto (es. nuovo PC) lo snooze viene rispettato.
//
// Le chiavi localStorage sono **scoped per operatorId** così su
// workstation condivise (caso reale dello studio) l'accumulo di ID
// di operatori diversi non si mischia. Quando cambia l'operatore
// loggato, le sue chiavi sono diverse → cache vuota → richiesta
// fresca al backend.
//
//   POPPED_KEY_PREFIX  → Set<notificationId> già mostrati nella sessione
//   SNOOZE_KEY_PREFIX  → Map<notificationId, snoozeUntilMs> rinvii cache locale
const POPPED_KEY_PREFIX = "notif_popped_ids_v3";
const SNOOZE_KEY_PREFIX = "notif_snoozed_until_v3";

function _scopedKey(prefix, opId) {
  // opId vuoto = chiave "guest" — comunque isolata dalle chiavi
  // operatore-specifiche, così un operatore non eredita lo stato
  // anonimo di un altro.
  return `${prefix}__${opId || "anon"}`;
}

function loadPopped(opId) {
  try {
    const raw = localStorage.getItem(_scopedKey(POPPED_KEY_PREFIX, opId));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function savePopped(opId, set) {
  try { localStorage.setItem(_scopedKey(POPPED_KEY_PREFIX, opId), JSON.stringify([...set])); } catch { /* quota */ }
}
function loadSnoozed(opId) {
  try {
    const raw = localStorage.getItem(_scopedKey(SNOOZE_KEY_PREFIX, opId));
    const obj = raw ? JSON.parse(raw) : {};
    return new Map(Object.entries(obj).map(([k, v]) => [k, Number(v) || 0]));
  } catch { return new Map(); }
}
function saveSnoozed(opId, map) {
  try {
    const obj = Object.fromEntries(map);
    localStorage.setItem(_scopedKey(SNOOZE_KEY_PREFIX, opId), JSON.stringify(obj));
  } catch { /* quota */ }
}

// One-shot migration: drop any leftover v1/v2 keys (globali, non-scoped)
// così le PC condivise non trascinano stato di operatori precedenti.
try {
  ["notif_popped_ids_v1", "notif_snoozed_until_v1", "notif_popped_ids_v2", "notif_snoozed_until_v2"].forEach((k) => {
    try { sessionStorage.removeItem(k); } catch { /* ignore */ }
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  });
} catch { /* SSR / private mode */ }

// Snooze duration choices shown in the popover (in minutes).
const SNOOZE_OPTIONS = [
  { label: "5 minuti",  minutes: 5 },
  { label: "15 minuti", minutes: 15 },
  { label: "30 minuti", minutes: 30 },
  { label: "1 ora",     minutes: 60 },
  { label: "Domani",    minutes: 24 * 60 },
];

const TYPE_ICONS = {
  Telefonata: Phone,
  Email: Mail,
  WhatsApp: MessageCircle,
  Presenza: UserCheck,
  "Pratica assegnata": Briefcase,
  "Nota pratica": FileText,
  "Promemoria scadenza": AlarmClock,
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return "ora";
    if (diffMin < 60) return `${diffMin} min fa`;
    const diffHrs = Math.round(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs} h fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

// Play a short pleasant chime using the Web Audio API. No file dependency.
// Wrapped in try/catch and a "user gesture" guard so first-load autoplay
// restrictions don't crash anything.
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const tones = [880, 1100]; // A5 → C#6 brief two-note ping
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.22);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.25);
    });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch { /* silent */ }
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // 'active' = unread + read (i.e. not done). 'done' = completed only.
  // 'all' shows the whole history including completed items.
  const [filter, setFilter] = useState("active");

  // Active popup QUEUE (bottom-right) — Phase A UX stabilization (17/02/2026):
  // notifications are no longer stacked vertically; only popups[0] is rendered
  // at any given time, and the next one slides in as soon as the current is
  // removed (close / mark read / mark done / snooze / open).
  //
  // Anti-spam (20/02/2026 — REVISIONE PERSISTENZA): popped + snoozed state
  // is now persisted in **localStorage** (was sessionStorage). Lo snooze
  // sopravvive a:
  //   - logout dell'operatore
  //   - chiusura del browser
  //   - riavvio del PC
  //   - lock/unlock di Windows
  // L'utente vede il popup SOLO alla data/ora programmata per il rinvio.
  const [popups, setPopups] = useState([]);
  // L'operatorId è disponibile via api client (`getCurrentOperatorId`).
  // Usiamo un ref così cambiamenti dell'utente loggato senza unmount
  // (caso teorico) sono comunque tracciati. Default "anon" finché il
  // client non ha caricato l'utente — il backend filtra comunque per
  // recipient, quindi nessun rischio di leak se opId è temporaneamente
  // sbagliato lato UI.
  const opIdRef = useRef(api.getCurrentOperatorId?.() || "anon");
  const poppedIdsRef = useRef(loadPopped(opIdRef.current));
  const snoozedUntilRef = useRef(loadSnoozed(opIdRef.current));
  // ID of the popup whose Rimanda menu is currently open (null = closed).
  const [snoozeMenuFor, setSnoozeMenuFor] = useState(null);
  // Posizione del menu Snooze nel viewport (calcolata via getBoundingClientRect
  // del bottone Rimanda quando il menu si apre). Renderizziamo il menu in
  // document.body via Portal per evitare clipping da `overflow-hidden` del
  // toast e da stacking-context intermedi.
  const [snoozeMenuPos, setSnoozeMenuPos] = useState(null);
  // Sub-popover "Personalizza" del menu Rimanda — quando true mostriamo
  // il calendario inline per scegliere un giorno specifico di re-pop.
  const [snoozeCalendarOpen, setSnoozeCalendarOpen] = useState(false);
  const snoozeBtnRefs = useRef(new Map());
  const popoverRef = useRef(null);
  // Audio is unlocked by Chrome only after the first user interaction.
  const audioUnlockedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await api.getNotifications({ limit: 30 });
      const list = data.items || [];
      setItems(list);
      setUnread(data.unreadCount || 0);

      // ── Snooze timer expiry sweep ──
      // Any notification whose local snooze window has elapsed becomes
      // eligible for re-pop. We drop it from BOTH snoozedUntil and from
      // poppedIds so the next `fresh` filter pass picks it up.
      const now = Date.now();
      let snoozeChanged = false;
      let poppedChanged = false;
      for (const [id, until] of snoozedUntilRef.current.entries()) {
        if (until <= now) {
          snoozedUntilRef.current.delete(id);
          snoozeChanged = true;
          if (poppedIdsRef.current.delete(id)) poppedChanged = true;
        }
      }
      if (snoozeChanged) saveSnoozed(opIdRef.current, snoozedUntilRef.current);
      if (poppedChanged) savePopped(opIdRef.current, poppedIdsRef.current);

      // Identify newly-arrived unread notifications that haven't been popped
      // in this session yet AND aren't currently muted by a snooze window.
      const fresh = list.filter((n) =>
        !n.read &&
        !poppedIdsRef.current.has(n.id) &&
        !(snoozedUntilRef.current.get(n.id) > now)
      );
      if (fresh.length > 0) {
        fresh.forEach((n) => poppedIdsRef.current.add(n.id));
        savePopped(opIdRef.current, poppedIdsRef.current);
        setPopups((prev) => {
          const existing = new Set(prev.map((p) => p.id));
          const merged = [...prev];
          fresh.forEach((n) => { if (!existing.has(n.id)) merged.push(n); });
          // Cap queue depth to the 5 most recent unhandled notifications.
          // Older items are dropped from the popup queue but remain in the
          // bell dropdown (full archive).
          return merged.slice(-5);
        });
        if (audioUnlockedRef.current) playChime();
      }
    } catch (e) {
      // Silent — notifications must never crash the header
    }
  }, []);

  // Unlock audio after first user gesture (Chrome / Safari policy)
  useEffect(() => {
    const unlock = () => {
      audioUnlockedRef.current = true;
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Initial load + poll every 30s.
  // Polling implicitly drains the snooze-expiry sweep, so snoozes of
  // 5/15/30/60 min wake up within ±30s of their scheduled time (good
  // enough for operational reminders, no extra timer needed).
  useEffect(() => {
    // Re-sincronizza opIdRef quando il client API ha caricato l'utente
    // (es. dopo login). Se l'opId cambia rispetto a quanto in cache,
    // ricarichiamo le cache scoped di popped/snoozed così non
    // ereditiamo lo stato dell'operatore precedente sullo stesso PC.
    const liveOpId = api.getCurrentOperatorId?.() || "anon";
    if (liveOpId !== opIdRef.current) {
      opIdRef.current = liveOpId;
      poppedIdsRef.current = loadPopped(liveOpId);
      snoozedUntilRef.current = loadSnoozed(liveOpId);
    }
    load();
    const id = setInterval(() => {
      const cur = api.getCurrentOperatorId?.() || "anon";
      if (cur !== opIdRef.current) {
        opIdRef.current = cur;
        poppedIdsRef.current = loadPopped(cur);
        snoozedUntilRef.current = loadSnoozed(cur);
      }
      load();
    }, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleMarkRead = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.markNotificationRead(id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
      // Marking as read also dismisses any open popup for this notification
      setPopups(prev => prev.filter(p => p.id !== id));
    } catch { /* ignore */ }
  };

  const handleMarkAll = async () => {
    try {
      await api.markAllNotificationsRead();
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
      setPopups([]);
    } catch { /* ignore */ }
  };

  const handleMarkDone = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.markNotificationDone(id);
      // Done implies read → if the notification was still unread, decrement
      setItems(prev => {
        let touchedUnread = false;
        const next = prev.map(n => {
          if (n.id !== id) return n;
          if (!n.read) touchedUnread = true;
          return { ...n, read: true, done: true };
        });
        if (touchedUnread) setUnread(u => Math.max(0, u - 1));
        return next;
      });
      // Done notifications must disappear from the active popup stack
      setPopups(prev => prev.filter(p => p.id !== id));
    } catch { /* ignore */ }
  };

  const handleClearDone = async () => {
    const doneCount = items.filter(n => n.done).length;
    if (doneCount === 0) return;
    if (!window.confirm(
      `Vuoi rimuovere ${doneCount} notific${doneCount === 1 ? "a completata" : "he completate"} dallo storico? L'azione non è reversibile.`
    )) return;
    try {
      await api.clearDoneNotifications();
      setItems(prev => prev.filter(n => !n.done));
    } catch { /* ignore */ }
  };

  // ESC key chiude il menu Snooze. Implementato a livello documento (non
  // sul button) per intercettare l'evento anche quando il focus è dentro
  // la dropdown — vincolo accessibility A11Y. Si auto-rimuove quando il
  // menu si chiude per evitare leak di listener.
  useEffect(() => {
    if (!snoozeMenuFor) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setSnoozeMenuFor(null);
        setSnoozeMenuPos(null);
        setSnoozeCalendarOpen(false);
      }
    };
    // Su resize/scroll chiudiamo il menu: la posizione fixed calcolata via
    // getBoundingClientRect diventerebbe stale e non vogliamo "menu fantasma"
    // mentre l'utente naviga sotto.
    const onWindowChange = () => {
      setSnoozeMenuFor(null);
      setSnoozeMenuPos(null);
      setSnoozeCalendarOpen(false);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [snoozeMenuFor]);

  // Sicurezza: se il popup attualmente menu-aperto sparisce dalla coda
  // (es. la queue avanza per un'azione esterna), chiudi il menu.
  useEffect(() => {
    if (snoozeMenuFor && !popups.some((p) => p.id === snoozeMenuFor)) {
      setSnoozeMenuFor(null);
      setSnoozeMenuPos(null);
      setSnoozeCalendarOpen(false);
    }
  }, [popups, snoozeMenuFor]);

  /**
   * "Rimanda" — local-session snooze.
   *
   * The operator picks a delay from the popover (5/15/30 min, 1 h, Domani).
   * We store `snoozeUntil = now + delay` in sessionStorage and drop the
   * popup from the queue. The polling loop's expiry sweep will resurrect
   * it from `poppedIds` once the timer elapses, so the popup pops back
   * automatically. We DO NOT mark it as read — the operator wants to be
   * reminded again later.
   *
   * For deadline reminders we also try the backend snooze call (which
   * advances the deadline's `nextReminderAt` field) BUT only as a defensive
   * extra; the popup re-pop is controlled entirely by the frontend timer
   * so the 5/15/30 min granularity works regardless of cron timing.
   * 404 from the backend (orphan source) is silently absorbed.
   */
  const handleSnoozeWithDelay = (notif, minutes, e) => {
    if (e) e.stopPropagation();
    const until = Date.now() + minutes * 60 * 1000;
    // 1) PERSIST BACKEND — fonte di verità (bug fix 22/02/2026).
    //    Setta `snoozedUntil` sul documento notifica del solo recipient
    //    corrente. Il filtro lato `GET /api/notifications` esclude
    //    automaticamente le notifiche con `snoozedUntil > now`, quindi
    //    al prossimo polling il popup NON ricompare finché il timer
    //    non scade — su QUALUNQUE PC l'operatore acceda.
    api.snoozeNotification(notif.id, { minutes }).catch((err) => {
      if (err?.status !== 404) {
        // eslint-disable-next-line no-console
        console.warn("Backend snooze notification failed:", err);
      }
    });

    // 2) Cache localStorage anti-flicker (scoped per operatorId):
    //    evita il re-pop di 30s nell'intervallo tra click e prossimo
    //    polling. NON è la fonte di verità — al primo refresh il
    //    backend la riconferma. Se l'utente passa a un altro PC,
    //    cache vuota ma backend filtra → popup comunque nascosto.
    snoozedUntilRef.current.set(notif.id, until);
    saveSnoozed(opIdRef.current, snoozedUntilRef.current);
    poppedIdsRef.current.delete(notif.id);
    savePopped(opIdRef.current, poppedIdsRef.current);

    // 3) Optimistic dismiss from the visible queue.
    setPopups((prev) => prev.filter((p) => p.id !== notif.id));
    setSnoozeMenuFor(null);
    setSnoozeMenuPos(null);
    setSnoozeCalendarOpen(false);

    // 4) Per deadline reminders aggiorniamo anche il nextReminderAt
    //    dello scadenziario sorgente — così il cron non rigenera
    //    una nuova notifica prima dello snooze. 404 = pratica orfana,
    //    silent.
    if (notif.kind === "deadline_reminder" && notif.sourceId) {
      api.snoozeCustomDeadline(notif.sourceId).catch((err) => {
        if (err?.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("Backend snooze deadline failed:", err);
        }
      });
    }

    // 5) Discreet user feedback.
    try {
      const label =
        minutes >= 60 * 24 ? "domani" :
        minutes >= 60     ? `${Math.round(minutes / 60)}h` :
                            `${minutes} min`;
      toast?.success?.(`Promemoria rimandato di ${label}`);
    } catch { /* sonner not available */ }
  };

  /**
   * "Personalizza" — snooze fino al giorno scelto dal calendario.
   *
   * Il giorno selezionato viene normalizzato all'inizio della
   * mattinata lavorativa (08:00 locale) così il popup ricompare
   * all'apertura ufficio del giorno indicato e non a mezzanotte.
   * Se l'utente sceglie oggi stesso, snooziamo comunque almeno
   * un minuto per dare un feedback coerente.
   */
  const handleSnoozeUntilDate = (notif, date) => {
    if (!date) return;
    const target = new Date(date);
    target.setHours(8, 0, 0, 0);
    const now = new Date();
    const ms = target.getTime() - now.getTime();
    const minutes = Math.max(1, Math.round(ms / 60000));
    handleSnoozeWithDelay(notif, minutes);
    try {
      toast?.success?.(
        `Promemoria rimandato a ${target.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}`
      );
    } catch { /* sonner not available */ }
  };

  /**
   * "Apri scadenza" — close the popup and route the operator to the
   * Scadenze tab. We use a global `window.dispatchEvent` rather than
   * tightly coupling NotificationBell to App.js routing state.
   */
  const handleOpenDeadline = (notif) => {
    setPopups(prev => prev.filter(p => p.id !== notif.id));
    // Optimistically mark read so the bell badge updates immediately.
    if (!notif.read) {
      api.markNotificationRead(notif.id).catch(() => {});
      setItems(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    }
    window.dispatchEvent(new CustomEvent("open-scadenze-tab", {
      detail: { deadlineId: notif.sourceId || "" },
    }));
  };

  /**
   * ─── Click sul corpo del popup (07/07/2026 — spec urgente) ─────────
   *
   * Portiamo l'operatore direttamente alla nota/attività notificata.
   * Il routing è per `kind` della notifica (vedi _emit_operator_notification
   * lato backend):
   *
   *   • deadline_reminder  → Scadenzario (sub-tab Clienti)
   *   • diary_contact      → Attività Operative → Diario Giornaliero
   *   • practice_assigned  → Registro Pratiche (+ caseId per contesto)
   *   • practice_note      → idem practice_assigned
   *   • daily_practice_*   → Attività Operative → Lavoro Quotidiano
   *   • fallback           → Registro Pratiche se c'è caseId, altrimenti
   *                          Clienti se c'è clientId, altrimenti nulla.
   *
   * NON apre nuovi dialog — usiamo i tab esistenti. Rispetta la spec
   * "nessun refactor generale". Il popup viene chiuso e la notifica
   * segnata come letta (best-effort) come già facciamo per "Apri scadenza".
   */
  const handleOpenNotification = (notif) => {
    if (!notif) return;
    setPopups(prev => prev.filter(p => p.id !== notif.id));
    setSnoozeMenuFor(null);
    setSnoozeMenuPos(null);
    setSnoozeCalendarOpen(false);
    // Optimistic mark-read (fire-and-forget). Non blocca il routing.
    if (!notif.read) {
      api.markNotificationRead(notif.id).catch(() => {});
      setItems(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    }
    const kind = (notif.kind || notif.sourceType || "").toString();
    const detail = {
      notificationId: notif.id,
      sourceId: notif.sourceId || "",
      caseId: notif.caseId || "",
      caseNumber: notif.caseNumber || "",
      clientId: notif.clientId || "",
      clientName: notif.clientName || "",
      kind,
    };
    if (kind === "deadline_reminder") {
      window.dispatchEvent(new CustomEvent("open-scadenze-tab", {
        detail: { deadlineId: notif.sourceId || "" },
      }));
    } else if (kind === "diary_contact") {
      window.dispatchEvent(new CustomEvent("gi:open-diary-from-notification", { detail }));
    } else if (kind && kind.indexOf("daily_practice") === 0) {
      window.dispatchEvent(new CustomEvent("gi:open-lavoro-from-notification", { detail }));
    } else if (kind === "practice_assigned" || kind === "practice_note") {
      window.dispatchEvent(new CustomEvent("gi:open-practice-from-notification", { detail }));
    } else if (notif.caseId) {
      window.dispatchEvent(new CustomEvent("gi:open-practice-from-notification", { detail }));
    } else if (notif.clientId) {
      window.dispatchEvent(new CustomEvent("gi:open-client-from-notification", { detail }));
    }
    // Se nulla combacia, il popup si chiude comunque e l'operatore
    // vede la notifica nella campanella con la nota completa.
  };


  const handleDismissPopup = (id) => {
    // Manually closing a popup hides it for THIS session. `poppedIds`
    // already contains the id (added on first pop), so the polling loop
    // won't re-show it. The notification remains unread → still visible
    // in the bell dropdown until explicitly handled.
    setPopups(prev => prev.filter(p => p.id !== id));
    setSnoozeMenuFor(null);
    setSnoozeMenuPos(null);
    setSnoozeCalendarOpen(false);
  };

  return (
    <>
      <div className="relative" ref={popoverRef}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100 transition-colors"
          aria-label="Notifiche"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5 text-slate-700" />
          {unread > 0 && (
            <Badge
              className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold leading-none flex items-center justify-center pointer-events-none"
              data-testid="notification-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </button>

        {open && (
          <div
            className="absolute right-0 mt-2 w-[360px] max-h-[480px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col"
            data-testid="notification-dropdown"
          >
            <div className="px-3 py-2 border-b flex items-center justify-between bg-slate-50 rounded-t-lg">
              <span className="text-sm font-semibold text-slate-700">Notifiche</span>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAll}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    data-testid="notification-mark-all"
                  >
                    <CheckCheck className="h-3 w-3" /> Segna tutte come lette
                  </button>
                )}
                <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Filter tabs — let the recipient triage unread/active vs the
                completed history. Counts are computed from the local `items`
                list so they update immediately on every mark-read / mark-done. */}
            <div className="flex items-stretch border-b text-xs bg-white sticky top-0">
              {[
                { key: "active",    label: "Attive",     count: items.filter(n => !n.done).length },
                { key: "done",      label: "Completate", count: items.filter(n =>  n.done).length },
                { key: "all",       label: "Tutte",      count: items.length },
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setFilter(tab.key)}
                  data-testid={`notification-tab-${tab.key}`}
                  className={
                    "flex-1 px-2 py-1.5 transition-colors flex items-center justify-center gap-1 " +
                    (filter === tab.key
                      ? "text-slate-800 font-semibold border-b-2 border-blue-600 bg-blue-50/30"
                      : "text-slate-500 hover:text-slate-700 border-b-2 border-transparent")
                  }
                >
                  {tab.label}
                  <span className={
                    "rounded-full px-1.5 leading-none " +
                    (filter === tab.key
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-100 text-slate-500")
                  }>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {(() => {
                const filtered =
                  filter === "active" ? items.filter(n => !n.done)
                  : filter === "done" ? items.filter(n =>  n.done)
                  : items;
                if (filtered.length === 0) {
                  const emptyLabel =
                    filter === "active" ? "Nessuna notifica attiva"
                    : filter === "done" ? "Nessuna notifica completata"
                    : "Nessuna notifica";
                  return (
                    <div className="p-8 text-center text-sm text-slate-400">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-slate-200" />
                      {emptyLabel}
                    </div>
                  );
                }
                return filtered.map(n => {
                const Ic = TYPE_ICONS[n.contactType] || Bell;
                // Visual states:
                //   unread → blue tint, blue icon, bold label, "letto" + "fatta" buttons
                //   read   → white bg, gray icon, normal label, "fatta" button only
                //   done   → green tint, green check badge, struck label, NO action buttons
                const stateClasses = n.done
                  ? "bg-emerald-50/60 hover:bg-emerald-50"
                  : n.read
                    ? "hover:bg-slate-50"
                    : "bg-blue-50/60 hover:bg-blue-50";
                const labelClasses = n.done
                  ? "text-slate-500 line-through"
                  : n.read
                    ? "text-slate-600"
                    : "text-slate-800 font-medium";
                const iconClasses = n.done
                  ? "text-emerald-600"
                  : n.read
                    ? "text-slate-400"
                    : "text-blue-600";
                return (
                  <div
                    key={n.id}
                    className={`px-3 py-2.5 cursor-pointer transition-colors ${stateClasses}`}
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => !n.read && !n.done && handleMarkRead(n.id)}
                  >
                    <div className="flex items-start gap-2.5">
                      <Ic className={`h-4 w-4 mt-0.5 shrink-0 ${iconClasses}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm ${labelClasses} truncate flex items-center gap-1`}>
                            {n.done && <CheckCheck className="h-3 w-3 text-emerald-600 shrink-0" />}
                            {n.contactType}
                            {n.clientName ? <span className="text-slate-500 font-normal no-underline"> · {n.clientName}</span> : null}
                          </span>
                          <span className="text-[10px] text-slate-400 shrink-0">{formatWhen(n.createdAt)}</span>
                        </div>
                        {n.content && (
                          <p className={`text-xs mt-0.5 line-clamp-2 ${n.done ? "text-slate-400" : "text-slate-600"}`}>{localizeIsoDatesInText(n.content)}</p>
                        )}
                        <div className="flex items-center justify-between mt-1 gap-2">
                          <span className="text-[11px] text-slate-400 truncate">
                            da {n.senderOperatorName || "Operatore"}
                            {n.caseNumber ? ` · pratica ${n.caseNumber}` : ""}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            {!n.read && !n.done && (
                              <button
                                type="button"
                                onClick={(e) => handleMarkRead(n.id, e)}
                                className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5"
                                data-testid={`notification-mark-read-${n.id}`}
                              >
                                <Check className="h-3 w-3" /> letto
                              </button>
                            )}
                            {!n.done && (
                              <button
                                type="button"
                                onClick={(e) => handleMarkDone(n.id, e)}
                                className="text-[11px] text-emerald-700 hover:underline flex items-center gap-0.5"
                                data-testid={`notification-mark-done-${n.id}`}
                              >
                                <CheckCheck className="h-3 w-3" /> fatto
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
                });
              })()}
            </div>

            {/* Footer: bulk-clear completed notifications. Only visible when
                there's at least one done item AND the user is on the
                Completate/Tutte tabs (where the action is semantically
                relevant). */}
            {items.some(n => n.done) && filter !== "active" && (
              <div className="border-t bg-slate-50 px-3 py-2 flex items-center justify-between text-xs">
                <span className="text-slate-500">
                  {items.filter(n => n.done).length} notifich{items.filter(n => n.done).length === 1 ? "a completata" : "e completate"}
                </span>
                <button
                  type="button"
                  onClick={handleClearDone}
                  className="text-red-600 hover:text-red-700 hover:underline flex items-center gap-1 font-medium"
                  data-testid="notification-clear-done"
                  title="Rimuove dallo storico le notifiche completate"
                >
                  <Trash2 className="h-3 w-3" /> Pulisci completate
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Queued single-toast (Phase A UX stabilization, 17/02/2026) ───
          Behavior contract (kept stable to avoid breaking the existing
          handlers — load(), handleMarkRead/Done/Dismiss/Open/Snooze
          continue to mutate `popups` the same way they did before):

            * `popups` is the QUEUE. The original stack-of-5 rendering
              has been replaced by a single visible toast at any given
              moment. The next toast becomes visible automatically as
              soon as the current one is removed from the queue (close,
              remind, done, mark read).
            * Priority color (red/amber/blue/green) is derived from the
              notification kind + `deadlineDueDate` so overdue deadlines
              dominate the visual hierarchy.
            * A "1 di N" counter shows the queue depth.
            * Enter animation = fade + slide from right; exit animation
              relies on the React unmount via Radix-like classes (we
              key the wrapper on the visible id so swapping triggers a
              fresh entrance for the next item).
            * Bell dropdown remains the full archive — popups never
              act as history.
      */}
      {/* Popup notifica corrente — renderizzata via `createPortal`
          in `document.body` (fix 22/02/2026 crash `removeChild`).
          Motivo: il popup era inline nel tree React di NotificationBell
          (sotto AppHeader). Quando l'utente cambiava sub-tab di
          Attività Operative SIMULTANEAMENTE allo scadere del polling
          notifiche (30s) o all'auto-dismiss di un toast, React 19
          processava in un unico commit phase l'unmount del subtab
          pesante (Diario 6754 note) e il re-key del popup
          (`key={current.id}`). Il commit batch falliva con
          `removeChild` perché il DOM era già stato mutato da
          un'animazione CSS in volo. Portalando in body, il popup vive
          in un sotto-albero React isolato che non corre con la
          riconciliazione del main tree. */}
      {popups.length > 0 && createPortal((() => {
        const current = popups[0];
        const remaining = popups.length;
        const Ic = TYPE_ICONS[current.contactType] || Bell;

        // ── Priority palette (req #4) ─────────────────────────────
        // overdue=red · today=amber · upcoming=blue · default=blue
        let priority = "info"; // "overdue" | "today" | "info"
        let priorityLabel = "Notifica";
        if (current.kind === "deadline_reminder" && current.deadlineDueDate) {
          const today = new Date().toISOString().slice(0, 10);
          if (current.deadlineDueDate < today) {
            priority = "overdue";
            priorityLabel = "Scadenza in ritardo";
          } else if (current.deadlineDueDate === today) {
            priority = "today";
            priorityLabel = "Scadenza oggi";
          } else {
            priorityLabel = "Promemoria scadenza";
          }
        } else if (current.kind === "daily_practice_overdue") {
          priority = "overdue";
          priorityLabel = "Pratica in ritardo";
        } else {
          priorityLabel = current.contactType || "Notifica";
        }
        const PRIO_STYLES = {
          overdue: {
            header: "bg-red-600 text-white",
            ring: "border-red-300",
            badge: "bg-red-100 text-red-800",
          },
          today: {
            header: "bg-amber-500 text-white",
            ring: "border-amber-300",
            badge: "bg-amber-100 text-amber-800",
          },
          info: {
            header: "bg-blue-600 text-white",
            ring: "border-blue-300",
            badge: "bg-blue-100 text-blue-800",
          },
        };
        const styles = PRIO_STYLES[priority];

        return (
          <div
            className="fixed top-4 right-4 z-[100] w-[420px] max-w-[calc(100vw-2rem)] pointer-events-none"
            data-testid="notification-popup-stack"
            data-queue-size={remaining}
          >
            <div
              key={current.id}  /* re-keying triggers fresh enter animation */
              className={`pointer-events-auto bg-white border ${styles.ring} rounded-lg shadow-xl overflow-hidden transition-all duration-300 animate-in slide-in-from-right-6 fade-in`}
              data-testid={`notification-popup-${current.id}`}
              data-priority={priority}
              role="alert"
            >
              {/* ── compact header ── */}
              <div className={`flex items-center justify-between px-3 py-1.5 text-sm font-medium ${styles.header}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Ic className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{priorityLabel}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {remaining > 1 && (
                    <span
                      className="text-[11px] bg-white/20 px-1.5 py-0.5 rounded-full"
                      data-testid="notification-popup-queue-counter"
                      aria-label={`${remaining} notifiche in coda`}
                    >
                      1 di {remaining}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDismissPopup(current.id)}
                    className="text-white/80 hover:text-white"
                    aria-label="Chiudi"
                    data-testid={`notification-popup-close-${current.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* ── compact body — cliccabile per aprire la nota (07/07/2026) ── */}
              <div
                className="px-3 py-2 space-y-1 cursor-pointer hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-inset"
                onClick={() => handleOpenNotification(current)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenNotification(current);
                  }
                }}
                role="button"
                tabIndex={0}
                title="Clicca per aprire la nota/attività"
                data-testid={`notification-popup-body-${current.id}`}
              >
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {current.clientName || current.contactType || "—"}
                </p>
                {current.content && (
                  <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">
                    {localizeIsoDatesInText(current.content)}
                  </p>
                )}
                {/* Phase 2 — deadline metadata strip, compact one-liner */}
                {current.kind === "deadline_reminder" && current.deadlineDueDate && (() => {
                  const today = new Date().toISOString().slice(0, 10);
                  const ms = (new Date(current.deadlineDueDate) - new Date(today));
                  const d = Math.round(ms / 86400000);
                  const daysLine = d < 0
                    ? `scaduta da ${Math.abs(d)} g`
                    : d === 0 ? "scade oggi" : `mancano ${d} g`;
                  return (
                    <p className="text-[11px] text-slate-500">
                      Scade {formatDate(current.deadlineDueDate)} · {daysLine}
                      {current.deadlineAreaType ? ` · ${current.deadlineAreaType}` : ""}
                    </p>
                  );
                })()}
                <p className="text-[11px] text-slate-400">
                  da {current.senderOperatorName || "Sistema"}
                  {current.caseNumber ? ` · pratica ${current.caseNumber}` : ""}
                  {current.createdAt ? ` · ${formatWhen(current.createdAt)}` : ""}
                </p>

                {/* ── compact action row — NON deve triggerare l'apertura ── */}
                <div className="flex justify-end gap-1.5 pt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  {/* "Rimanda" è disponibile per ogni tipo: l'operatore sceglie
                      quando il popup deve ricomparire. Il backend snooze viene
                      chiamato in aggiunta solo per deadline_reminder.
                      Il menu vero e proprio è renderizzato via Portal in
                      document.body (vedi sotto) per evitare clipping da
                      overflow-hidden del toast e stacking-context intermedi. */}
                  <button
                    type="button"
                    ref={(el) => {
                      if (el) snoozeBtnRefs.current.set(current.id, el);
                      else snoozeBtnRefs.current.delete(current.id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const isOpen = snoozeMenuFor === current.id;
                      if (isOpen) {
                        setSnoozeMenuFor(null);
                        setSnoozeMenuPos(null);
                        setSnoozeCalendarOpen(false);
                        return;
                      }
                      // Popup ora ancorato in alto a destra (20/02/2026):
                      // il menu apre verso il BASSO, allineato al lato
                      // DESTRO del bottone Rimanda (così non sborda dal
                      // viewport sul lato destro). Usiamo `right` invece
                      // di `left` per inchiodare il menu al bordo destro
                      // del bottone.
                      const btn = e.currentTarget;
                      const r = btn.getBoundingClientRect();
                      setSnoozeMenuPos({
                        top: r.bottom + 6,
                        right: window.innerWidth - r.right,
                      });
                      setSnoozeMenuFor(current.id);
                      setSnoozeCalendarOpen(false);
                    }}
                    className="px-2.5 py-1 text-xs rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 flex items-center gap-1"
                    data-testid={`notification-popup-snooze-${current.id}`}
                    aria-haspopup="menu"
                    aria-expanded={snoozeMenuFor === current.id}
                  >
                    <MoonStar className="h-3 w-3" /> Rimanda
                  </button>

                  {current.kind === "deadline_reminder" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleOpenDeadline(current)}
                        className="px-2.5 py-1 text-xs rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 flex items-center gap-1"
                        data-testid={`notification-popup-open-${current.id}`}
                      >
                        <ExternalLink className="h-3 w-3" /> Apri
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMarkDone(current.id)}
                        className="px-2.5 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                        data-testid={`notification-popup-done-${current.id}`}
                      >
                        <CheckCheck className="h-3 w-3" /> Fatto
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleMarkRead(current.id)}
                        className="px-2.5 py-1 text-xs rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 flex items-center gap-1"
                        data-testid={`notification-popup-read-${current.id}`}
                      >
                        <Check className="h-3 w-3" /> Letta
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMarkDone(current.id)}
                        className="px-2.5 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                        data-testid={`notification-popup-done-${current.id}`}
                      >
                        <CheckCheck className="h-3 w-3" /> Fatto
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* ──────────────────────────────────────────────────────────────
          Menu "Rimanda" — renderizzato in document.body via Portal.

          Motivazione architetturale (root cause del bug precedente):
          il toast è racchiuso in una card con `overflow-hidden` e in più
          il vecchio wrapper relative+zIndex creava uno stacking context
          intrappolato. Risultato: il dropdown veniva o clippato dalla
          card o ricoperto da un overlay invisibile.
          Portal in body + posizionamento fixed calcolato → nessun
          overflow parent può tagliarlo, z-index deterministico al top
          level (overlay z-[105], menu z-[110], toast z-[100]).
          ────────────────────────────────────────────────────────────── */}
      {snoozeMenuFor && snoozeMenuPos && (() => {
        const targetId = snoozeMenuFor;
        const targetNotif = popups.find((p) => p.id === targetId);
        if (!targetNotif) return null;
        return createPortal(
          <>
            {/* Outside-click catcher full-viewport. Niente background, solo
                hit-test. z-[105] sopra al toast (z-[100]) ma sotto al menu. */}
            <div
              className="fixed inset-0 z-[105]"
              onClick={() => {
                setSnoozeMenuFor(null);
                setSnoozeMenuPos(null);
                setSnoozeCalendarOpen(false);
              }}
              data-testid="notification-popup-snooze-overlay"
            />
            <div
              className="fixed z-[110] min-w-[13rem] bg-white border border-slate-200 rounded-lg shadow-2xl ring-1 ring-slate-200/50 overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150"
              style={{ top: snoozeMenuPos.top, right: snoozeMenuPos.right }}
              role="menu"
              data-testid={`notification-popup-snooze-menu-${targetId}`}
            >
              <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50 border-b border-slate-100">
                Ricordami fra
              </div>
              {!snoozeCalendarOpen ? (
                <div className="py-1">
                  {SNOOZE_OPTIONS.map((opt) => (
                    <button
                      key={opt.minutes}
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        handleSnoozeWithDelay(targetNotif, opt.minutes, e);
                        setSnoozeMenuPos(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2.5 transition-colors duration-100"
                      data-testid={`notification-popup-snooze-${opt.minutes}-${targetId}`}
                    >
                      <Clock className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                      <span>{opt.label}</span>
                    </button>
                  ))}
                  {/* "Personalizza" — apre il calendario inline per scegliere
                      un giorno specifico (richiesta ufficio 20/02/2026).
                      Separatore visivo perché è un'azione diversa dalle
                      durate preimpostate sopra. */}
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSnoozeCalendarOpen(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2.5 transition-colors duration-100"
                      data-testid={`notification-popup-snooze-custom-${targetId}`}
                    >
                      <CalendarDays className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                      <span>Personalizza…</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-1" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between px-2 py-1">
                    <button
                      type="button"
                      onClick={() => setSnoozeCalendarOpen(false)}
                      className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1"
                      data-testid={`notification-popup-snooze-custom-back-${targetId}`}
                    >
                      ← Indietro
                    </button>
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                      Scegli giorno
                    </span>
                  </div>
                  <Calendar
                    mode="single"
                    locale={itLocale}
                    selected={undefined}
                    onSelect={(d) => {
                      if (d) handleSnoozeUntilDate(targetNotif, d);
                    }}
                    disabled={(d) => {
                      // Non si può rimandare nel passato. Confrontiamo solo
                      // la data (h 00:00:00), così "oggi" resta selezionabile.
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today;
                    }}
                    initialFocus
                    data-testid={`notification-popup-snooze-calendar-${targetId}`}
                  />
                </div>
              )}
            </div>
          </>,
          document.body
        );
      })()}
    </>
  );
}

